import React, { useState, useEffect, useCallback } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, TextField, Button, Grid, Alert,
  CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, InputAdornment, Chip, Stack, TablePagination,
  Autocomplete, Divider, Skeleton, Tab, Tabs,
} from "@mui/material";
import AddIcon        from "@mui/icons-material/Add";
import EditIcon       from "@mui/icons-material/Edit";
import DeleteIcon     from "@mui/icons-material/Delete";
import SearchIcon     from "@mui/icons-material/Search";
import BusinessIcon   from "@mui/icons-material/Business";
import LayersIcon     from "@mui/icons-material/Layers";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { isAutheticated } from "../auth/helper";
import { getAllCompanies, addCompany, updateCompany, deleteCompany,
         getAllGroups, createGroup, deleteGroup, addCompanyToGroup, removeCompanyFromGroup } from "./helper/adminapicall";

const EMPTY = { nse: "", nameOfCompany: "", bse: "" };

const TabPanel = ({ value, index, children }) =>
  value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;

const Companies = () => {
  const { admin, token } = isAutheticated();
  const [all,       setAll]       = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [search,    setSearch]    = useState("");
  const [loading,   setLoading]   = useState(true);
  const [msg,       setMsg]       = useState({ text: "", severity: "success" });

  // Add / Edit dialog
  const [dialog,    setDialog]    = useState(false);
  const [editOrig,  setEditOrig]  = useState(null); // original NSE when editing
  const [form,      setForm]      = useState(EMPTY);
  const [saving,    setSaving]    = useState(false);
  const [formErr,   setFormErr]   = useState("");

  // Delete confirm
  const [delTarget, setDelTarget] = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  // Groups
  const [groups,         setGroups]         = useState([]);
  const [loadingGroups,  setLoadingGroups]  = useState(true);
  const [newGroupName,   setNewGroupName]   = useState("");
  const [savingGroup,    setSavingGroup]    = useState(false);
  const [grpCompInputs,  setGrpCompInputs]  = useState({}); // groupId → company object | string
  const [delGroupTarget, setDelGroupTarget] = useState(null);
  const [viewGroup,      setViewGroup]      = useState(null); // group to preview
  const [viewSearch,     setViewSearch]     = useState("");

  // Pagination
  const [page,     setPage]     = useState(0);
  const PAGE_SIZE = 50;

  // Active tab
  const [tab, setTab] = useState(0);

  const flash = (text, severity = "success") => {
    setMsg({ text, severity });
    setTimeout(() => setMsg({ text: "" }), 4000);
  };

  const load = useCallback(() => {
    setLoading(true);
    getAllCompanies(admin._id, token)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setAll(list);
        setFiltered(list);
      })
      .finally(() => setLoading(false));
  }, [admin._id, token]);

  const loadGroups = useCallback(() => {
    setLoadingGroups(true);
    getAllGroups(admin._id, token)
      .then((data) => setGroups(Array.isArray(data) ? data : []))
      .finally(() => setLoadingGroups(false));
  }, [admin._id, token]);

  useEffect(() => { load(); loadGroups(); }, [load, loadGroups]);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) { setFiltered(all); setPage(0); return; }
    setFiltered(all.filter((c) =>
      c.nameOfCompany?.toLowerCase().includes(q) ||
      c.nse?.toLowerCase().includes(q) ||
      String(c.bse || "").includes(q)
    ));
    setPage(0);
  }, [search, all]);

  const openAdd = () => {
    setEditOrig(null);
    setForm(EMPTY);
    setFormErr("");
    setDialog(true);
  };

  const openEdit = (company) => {
    setEditOrig(company.nse);
    setForm({ nse: company.nse || "", nameOfCompany: company.nameOfCompany || "", bse: company.bse || "" });
    setFormErr("");
    setDialog(true);
  };

  const handleSave = () => {
    if (!form.nameOfCompany.trim()) { setFormErr("Company name is required"); return; }
    setFormErr(""); setSaving(true);
    const action = editOrig
      ? updateCompany(admin._id, token, editOrig, form)
      : addCompany(admin._id, token, form);

    action.then((data) => {
      setSaving(false);
      if (data?.error) { setFormErr(data.error); return; }
      setDialog(false);
      setPage(0);
      load();
      flash(editOrig ? "Company updated" : "Company added");
    }).catch(() => { setSaving(false); setFormErr("Request failed"); });
  };

  const handleDelete = () => {
    if (!delTarget) return;
    setDeleting(true);
    deleteCompany(admin._id, token, delTarget.nse)
      .then((data) => {
        setDeleting(false);
        if (data?.error) { flash(data.error, "error"); setDelTarget(null); return; }
        setDelTarget(null);
        load();
        flash("Company deleted");
      }).catch(() => { setDeleting(false); flash("Delete failed", "error"); });
  };

  // ── Group handlers ────────────────────────────────────────────────────────────
  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    setSavingGroup(true);
    const res = await createGroup(admin._id, token, { name });
    setSavingGroup(false);
    if (res?.error) { flash(res.error, "error"); return; }
    setNewGroupName("");
    loadGroups();
    flash(`Group "${name}" created`);
  };

  const handleDeleteGroup = async () => {
    if (!delGroupTarget) return;
    await deleteGroup(admin._id, token, delGroupTarget._id);
    setDelGroupTarget(null);
    loadGroups();
    flash(`Group "${delGroupTarget.name}" deleted`);
  };

  const handleAddCompanyToGroup = async (groupId) => {
    const company = grpCompInputs[groupId];
    if (!company) return;
    const body = typeof company === "string"
      ? { nameOfCompany: company, nse: "", bse: 0 }
      : { nameOfCompany: company.nameOfCompany, nse: company.nse || "", bse: company.bse || 0 };
    const res = await addCompanyToGroup(admin._id, token, groupId, body);
    if (res?.error) { flash(res.error, "error"); return; }
    setGrpCompInputs((prev) => ({ ...prev, [groupId]: null }));
    loadGroups();
  };

  const handleRemoveCompanyFromGroup = async (groupId, compId) => {
    await removeCompanyFromGroup(admin._id, token, groupId, compId);
    loadGroups();
  };

  return (
    <Base>
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 1.5,
                     bgcolor: tab === 0 ? "primary.main" : "secondary.dark",
                     display: "flex", alignItems: "center", justifyContent: "center",
                     transition: "background 0.2s" }}>
            {tab === 0
              ? <BusinessIcon sx={{ color: "#fff", fontSize: 20 }} />
              : <LayersIcon   sx={{ color: "#fff", fontSize: 20 }} />}
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>
              {tab === 0 ? "Companies" : "Company Groups"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {tab === 0
                ? `${all.length} companies · NSE / BSE listed`
                : `${groups.length} group${groups.length !== 1 ? "s" : ""} · sector / theme baskets for Compose`}
            </Typography>
          </Box>
        </Box>

        {/* Tab-specific action button */}
        {tab === 0 ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Company
          </Button>
        ) : (
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Group name e.g. IT-PACK"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreateGroup()}
              sx={{ width: { xs: 170, md: 210 } }}
            />
            <Button
              variant="contained"
              startIcon={savingGroup ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              onClick={handleCreateGroup}
              disabled={savingGroup || !newGroupName.trim()}
              sx={{ whiteSpace: "nowrap" }}
            >
              New Group
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Tabs ────────────────────────────────────────────────────────── */}
      <Tabs
        value={tab}
        onChange={(_, v) => { setTab(v); setSearch(""); }}
        sx={{ borderBottom: "1px solid", borderColor: "divider", mt: 2, mb: 0 }}
      >
        <Tab
          icon={<BusinessIcon fontSize="small" />}
          iconPosition="start"
          label={`Companies${all.length ? ` (${all.length})` : ""}`}
          sx={{ textTransform: "none", fontSize: "0.85rem", minHeight: 44 }}
        />
        <Tab
          icon={<LayersIcon fontSize="small" />}
          iconPosition="start"
          label={`Groups${groups.length ? ` (${groups.length})` : ""}`}
          sx={{ textTransform: "none", fontSize: "0.85rem", minHeight: 44 }}
        />
      </Tabs>

      {msg.text && <Alert severity={msg.severity} sx={{ mt: 2 }}>{msg.text}</Alert>}

      {/* ── Tab 0: Companies ─────────────────────────────────────────── */}
      <TabPanel value={tab} index={0}>
      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3, overflow: "hidden" }}>
        {/* Search bar */}
        <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by company name, NSE symbol or BSE code…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Company Name</TableCell>
                  <TableCell>NSE Symbol</TableCell>
                  <TableCell>BSE Code</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5, color: "text.secondary" }}>
                      {search ? "No companies match your search" : "No companies yet"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE).map((c, i) => (
                    <TableRow key={c.nse || i} hover>
                      <TableCell sx={{ color: "text.secondary", width: 50 }}>{page * PAGE_SIZE + i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{c.nameOfCompany}</TableCell>
                      <TableCell>
                        {c.nse ? <Chip label={c.nse} size="small" variant="outlined" color="primary" sx={{ fontFamily: "monospace", fontSize: "0.72rem" }} /> : "—"}
                      </TableCell>
                      <TableCell>
                        {c.bse ? <Chip label={c.bse} size="small" variant="outlined" sx={{ fontFamily: "monospace", fontSize: "0.72rem" }} /> : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => setDelTarget(c)} sx={{ color: "error.main" }}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {!loading && filtered.length > PAGE_SIZE && (
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          />
        )}

        {/* Result count */}
        {!loading && filtered.length > 0 && filtered.length <= PAGE_SIZE && (
          <Box sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="caption" color="text.secondary">
              {filtered.length === all.length ? `${all.length} companies` : `${filtered.length} of ${all.length} companies`}
            </Typography>
          </Box>
        )}
      </Paper>
      </TabPanel>

      {/* ── Tab 1: Groups ───────────────────────────────────────────────── */}
      <TabPanel value={tab} index={1}>
        {/* Group search */}
        {!loadingGroups && groups.length > 0 && (
          <TextField
            size="small"
            fullWidth
            placeholder="Search groups…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ mb: 2.5 }}
          />
        )}

        {/* Group cards */}
        {loadingGroups ? (
          <Grid container spacing={2}>
            {[1, 2, 3].map((n) => (
              <Grid item xs={12} sm={6} md={4} key={n}>
                <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 3 }} />
              </Grid>
            ))}
          </Grid>
        ) : groups.length === 0 ? (
          <Box sx={{ py: 5, textAlign: "center", color: "text.secondary",
                     border: "1px dashed", borderColor: "divider", borderRadius: 3 }}>
            No groups yet — type a name in the field above and click "New Group".
          </Box>
        ) : (() => {
          const q = search.trim().toLowerCase();
          const visibleGroups = q
            ? groups.filter((g) => g.name.toLowerCase().includes(q))
            : groups;
          return visibleGroups.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: "text.secondary",
                       border: "1px dashed", borderColor: "divider", borderRadius: 3 }}>
              No groups match "{search}"
            </Box>
          ) : (
          <Grid container spacing={2}>
            {visibleGroups.map((grp) => (
              <Grid item xs={12} sm={6} md={4} key={grp._id}>
                <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>

                  {/* Card header */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LayersIcon sx={{ fontSize: 16, color: "secondary.light", flexShrink: 0 }} />
                      <Typography variant="subtitle2" fontWeight={700}>{grp.name}</Typography>
                      <Chip label={grp.companies.length} size="small"
                        sx={{ height: 18, fontSize: "0.68rem", minWidth: 24, bgcolor: "rgba(255,255,255,0.08)" }} />
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Tooltip title="View all companies">
                        <IconButton size="small" sx={{ opacity: 0.6, "&:hover": { opacity: 1 } }}
                          onClick={() => { setViewGroup(grp); setViewSearch(""); }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete group">
                        <IconButton size="small" sx={{ color: "error.main", opacity: 0.7, "&:hover": { opacity: 1 } }}
                          onClick={() => setDelGroupTarget(grp)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  {/* Company chips */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7, mb: 1.5, minHeight: 28 }}>
                    {grp.companies.length > 0 ? (
                      grp.companies.map((c) => (
                        <Chip
                          key={c._id}
                          label={c.nse || c.nameOfCompany}
                          size="small"
                          title={c.nameOfCompany}
                          onDelete={() => handleRemoveCompanyFromGroup(grp._id, c._id)}
                          sx={{
                            bgcolor: "rgba(59,130,246,0.1)", color: "primary.light", fontSize: "0.72rem",
                            "& .MuiChip-deleteIcon": { color: "primary.light", opacity: 0.55, "&:hover": { opacity: 1 } },
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="caption" color="text.disabled">No companies yet</Typography>
                    )}
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Add company to group */}
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      sx={{ flex: 1 }}
                      options={all}
                      getOptionLabel={(o) => (typeof o === "string" ? o : o.nameOfCompany) || ""}
                      filterOptions={(opts, { inputValue }) => {
                        const q = inputValue.trim().toLowerCase();
                        if (!q) return [];
                        const res = [];
                        for (let i = 0; i < opts.length && res.length < 60; i++) {
                          const o = opts[i];
                          if ((o.nameOfCompany || "").toLowerCase().includes(q) ||
                              (o.nse || "").toLowerCase().includes(q)) res.push(o);
                        }
                        return res;
                      }}
                      value={grpCompInputs[grp._id] ?? null}
                      onChange={(_, val) => setGrpCompInputs((prev) => ({ ...prev, [grp._id]: val }))}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                            <span style={{ fontSize: "0.82rem" }}>{typeof option === "string" ? option : option.nameOfCompany}</span>
                            {typeof option !== "string" && option.nse && (
                              <span style={{ fontSize: "0.7rem", opacity: 0.55 }}>{option.nse}</span>
                            )}
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search and add company…"
                          inputProps={{ ...params.inputProps, style: { fontSize: "0.82rem" } }}
                        />
                      )}
                    />
                    <Tooltip title="Add to group">
                      <span>
                        <IconButton
                          size="small" color="primary"
                          onClick={() => handleAddCompanyToGroup(grp._id)}
                          disabled={!grpCompInputs[grp._id]}
                          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
          );
        })()}
      </TabPanel>
      <Dialog open={!!delGroupTarget} onClose={() => setDelGroupTarget(null)} maxWidth="xs" fullWidth disableRestoreFocus>
        <DialogTitle>Delete Group</DialogTitle>
        <DialogContent>
          <Typography>
            Delete group <strong>{delGroupTarget?.name}</strong>
            {delGroupTarget?.companies?.length > 0 && ` (${delGroupTarget.companies.length} compan${delGroupTarget.companies.length === 1 ? "y" : "ies"})`}?
            {" "}This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDelGroupTarget(null)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteGroup}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* ── View Group Dialog ─────────────────────────────────────────── */}
      <Dialog open={!!viewGroup} onClose={() => setViewGroup(null)} maxWidth="sm" fullWidth disableRestoreFocus>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LayersIcon sx={{ color: "secondary.light", fontSize: 20 }} />
            <span>{viewGroup?.name}</span>
            <Chip label={viewGroup?.companies?.length ?? 0} size="small"
              sx={{ height: 18, fontSize: "0.68rem", bgcolor: "rgba(255,255,255,0.08)" }} />
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {viewGroup?.companies?.length > 0 && (
            <TextField
              size="small" fullWidth autoFocus
              placeholder="Search companies in this group…"
              value={viewSearch}
              onChange={(e) => setViewSearch(e.target.value)}
              InputProps={{ startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
              )}}
              sx={{ mb: 2 }}
            />
          )}
          {!viewGroup?.companies?.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
              No companies in this group yet.
            </Typography>
          ) : (() => {
            const q = viewSearch.trim().toLowerCase();
            const filtered = viewGroup.companies.filter((c) =>
              !q ||
              (c.nameOfCompany || "").toLowerCase().includes(q) ||
              (c.nse || "").toLowerCase().includes(q)
            );
            return filtered.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                No companies match “{viewSearch}”
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40, color: "text.secondary" }}>#</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>NSE</TableCell>
                    <TableCell>BSE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((c, i) => (
                    <TableRow key={c._id} hover>
                      <TableCell sx={{ color: "text.secondary" }}>{i + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{c.nameOfCompany}</TableCell>
                      <TableCell>
                        {c.nse ? (
                          <Chip label={c.nse} size="small" variant="outlined" color="primary"
                            sx={{ fontFamily: "monospace", fontSize: "0.7rem" }} />
                        ) : "—"}
                      </TableCell>
                      <TableCell>
                        {c.bse ? (
                          <Chip label={c.bse} size="small" variant="outlined"
                            sx={{ fontFamily: "monospace", fontSize: "0.7rem" }} />
                        ) : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewGroup(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Add / Edit Dialog ─────────────────────────────────────────────── */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth disableRestoreFocus>
        <DialogTitle>{editOrig ? "Edit Company" : "Add Company"}</DialogTitle>
        <DialogContent>
          {formErr && <Alert severity="error" sx={{ mb: 2 }}>{formErr}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="Company Name *"
                value={form.nameOfCompany}
                onChange={(e) => setForm((f) => ({ ...f, nameOfCompany: e.target.value }))}
                autoFocus
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="NSE Symbol"
                value={form.nse}
                onChange={(e) => setForm((f) => ({ ...f, nse: e.target.value.toUpperCase() }))}
                inputProps={{ style: { fontFamily: "monospace" } }}
                helperText="e.g. RELIANCE"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="BSE Code" type="number"
                value={form.bse}
                onChange={(e) => setForm((f) => ({ ...f, bse: e.target.value }))}
                inputProps={{ style: { fontFamily: "monospace" } }}
                helperText="e.g. 500325  (0 = none)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialog(false)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={18} color="inherit" /> : editOrig ? "Save Changes" : "Add Company"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirm Dialog ─────────────────────────────────────────── */}
      <Dialog open={!!delTarget} onClose={() => setDelTarget(null)} maxWidth="xs" fullWidth disableRestoreFocus>
        <DialogTitle>Delete Company</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{delTarget?.nameOfCompany}</strong>? This cannot be undone and will affect company
            search in the compose form.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDelTarget(null)} sx={{ color: "text.secondary" }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Base>
  );
};

export default Companies;
